import React, { useCallback, useMemo, useRef, useState } from 'react';
import { usePrefixedNavigate as useNavigate } from '@edusense/navigation-utils';
import { CourseDetail, useCancelEnrollmentByCourseId, useGetCourseReviews, getCourseDraftId } from '@edusense/course-client';
import { CourseHeader, CourseCurriculum, type CourseHeaderData, type CoursePrice, ReviewList, Review } from '@edusense/features-course';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import { toast } from 'react-hot-toast';
import { COURSE_DETAIL_TEST_IDS } from '@edusense/shared-constants';
import { ConfirmModal } from '@edusense/shared-ui-components';
import { useAuth } from '@edusense/shared-auth-core';
import { Button } from '@edusense/shared-ui-kit';
import styled from '@emotion/styled';
import {
  TemplateContainer,
  MainContent,
  DescriptionContainer,
  DescriptionContent,
  ToggleButton,
  ButtonWrapper,
} from './CourseDetailTemplate.styles';
import { resolveOwnedState } from './courseOwnershipState';
import { visibleCourseDescription } from './courseDescription';

const ReviewSection = styled.section(({ theme }) => ({
  marginTop: theme.spacing.xl,
  padding: '24px',
  backgroundColor: 'var(--my-card-background)',
  border: '1px solid var(--theme-border)',
  borderRadius: 'var(--theme-radius-md)',

  h2: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    marginBottom: theme.spacing.lg,
    marginTop: 0,
    color: theme.text,
  },

  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    marginTop: theme.spacing.lg,
    padding: '20px',
  },
}));

const ReviewSectionHeader = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing.lg,

  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  },
}));

const ViewAllReviewsButton = styled(Button)(({ theme }) => ({
  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    width: '100%',
  },
}));

// 상세 데이터 인터페이스 수정: CourseDetail 타입을 직접 사용하거나 확장
interface CourseDetailTemplateProps {
  courseDetail: CourseDetail; // API 응답 타입 직접 사용
  onEnrollmentSuccess?: () => void; // 콜백 prop 추가 (optional)
  onCancelSuccess?: () => void; // 수강 취소 성공 콜백 추가
  onAuthorClick?: () => void; // 강사 프로필로 이동 콜백
  ownedOverride?: boolean | null;
  currentEnrollmentId?: string | null;
  availableLanguages?: string[];
  selectedLanguage?: string;
  onLanguageChange?: (languageCode: string) => void;
  isPreviewMode?: boolean; // Draft 미리보기 모드 여부
}

const LANGUAGE_LABELS: Record<string, string> = {
  ko: '한국어',
  'ko-kr': '한국어',
  en: 'English',
  'en-us': 'English (US)',
  'en-gb': 'English (UK)',
  ja: '日本語',
  zh: '中文(간체)',
  'zh-cn': '中文(간체)',
  'zh-tw': '中文(번체)',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
};

const normalizeLanguageCode = (code?: string | null): string => {
  if (!code) {
    return 'ko';
  }
  const trimmed = `${code}`.trim();
  if (!trimmed) {
    return 'ko';
  }
  return trimmed.toLowerCase();
};

const getLanguageLabel = (languageCode: string): string => {
  const normalized = normalizeLanguageCode(languageCode);
  return LANGUAGE_LABELS[normalized] ?? normalized.toUpperCase();
};

const CourseDetailTemplate: React.FC<CourseDetailTemplateProps> = ({
  courseDetail,
  onEnrollmentSuccess, // prop 받기
  onCancelSuccess, // 수강 취소 성공 콜백 받기
  onAuthorClick,
  ownedOverride = null,
  currentEnrollmentId = null,
  availableLanguages = [],
  selectedLanguage,
  onLanguageChange,
  isPreviewMode = false, // Draft 미리보기 모드
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth(); // 현재 로그인한 사용자 정보
  const backendOwned = courseDetail.summary?.owned === true;
  const resolvedOwned = resolveOwnedState(backendOwned, ownedOverride);
  // Open-access courses let lectures open without enrolling; enrollment stays as it is.
  const openAccess = courseDetail.summary?.openAccess === true;
  const courseIdForCancellation = courseDetail.summary?.id;
  const progressRateValue = courseDetail.summary?.progressRate ?? 0;
  const isCompleted = useMemo(() => progressRateValue >= 1, [progressRateValue]);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const cancelResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Orval 수강 취소 mutation hook
  const cancelEnrollmentMutation = useCancelEnrollmentByCourseId();

  // 리뷰 데이터 가져오기 (최대 3개만 미리보기)
  const courseIdForReviews = courseDetail.summary?.id;
  const {
    data: reviewsData,
    isLoading: reviewsLoading
  } = useGetCourseReviews(
    courseIdForReviews!,
    {
      page: '0',
      size: '3' // 미리보기는 3개만
    },
    {
      query: {
        enabled: !!courseIdForReviews,
        select: (response) => {
          const reviewResponses = response.data.reviews ?? [];

          // ReviewResponse -> Review 변환
          const transformedReviews: Review[] = reviewResponses.map(res => {
            const ratingValue = parseInt(res.rating, 10);

            return {
              id: res.id,
              author: res.reviewer,
              rating: !isNaN(ratingValue) ? ratingValue : 0,
              comment: res.comment,
              createdAt: res.createdAt,
            };
          });

          return transformedReviews;
        }
      }
    }
  );

  const handleNavigateToReviews = useCallback(() => {
    if (courseIdForReviews == null) {
      console.warn('[CourseDetailTemplate] Course ID missing, cannot navigate to reviews page.');
      return;
    }
    navigate(`/learn/course/${courseIdForReviews}/reviews`);
  }, [courseIdForReviews, navigate]);

  const reviews = reviewsData ?? [];
  const hasReviews = reviews.length > 0;

  // 현재 사용자가 강의 작성자인지 확인
  const isInstructor = useMemo(() => {
    if (!user || !courseDetail.summary?.instructor?.basicInfo?.id) {
      return false;
    }
    // user.id는 string 또는 number일 수 있으므로 둘 다 비교
    const currentUserId = String(user.id);
    const instructorId = String(courseDetail.summary.instructor.basicInfo.id);
    return currentUserId === instructorId;
  }, [user, courseDetail.summary?.instructor?.basicInfo?.id]);

  const normalizedLanguageOptions = useMemo(() => {
    const combined = [
      courseDetail.summary?.defaultLanguage,
      ...(courseDetail.summary?.supportedLanguages ?? []),
      ...availableLanguages,
    ];

    const normalized = combined
      .map(normalizeLanguageCode)
      .filter((lang): lang is string => Boolean(lang));

    const unique = Array.from(new Set(normalized));
    return unique.length > 0 ? unique : ['ko'];
  }, [availableLanguages, courseDetail.summary?.defaultLanguage, courseDetail.summary?.supportedLanguages]);

  const normalizedSelectedLanguage = useMemo(() => {
    const normalized = normalizeLanguageCode(selectedLanguage);
    if (normalizedLanguageOptions.includes(normalized)) {
      return normalized;
    }
    return normalizedLanguageOptions[0];
  }, [normalizedLanguageOptions, selectedLanguage]);

  const hasLanguageOptions = normalizedLanguageOptions.length > 0;
  const isMultiLanguage = normalizedLanguageOptions.length > 1;

  const handleTemplateEnrollmentSuccess = () => {
    console.log('[CourseDetailTemplate] Enrollment success callback triggered');
    console.log('[CourseDetailTemplate] Current backendOwned:', backendOwned);
    console.log('[CourseDetailTemplate] Current ownedOverride:', ownedOverride);
    console.log('[CourseDetailTemplate] Resolved owned state:', resolvedOwned);
    if (onEnrollmentSuccess) {
      console.log('[CourseDetailTemplate] Calling onEnrollmentSuccess');
      onEnrollmentSuccess();
    } else {
      console.log('[CourseDetailTemplate] onEnrollmentSuccess callback is not provided');
    }
  };

  const handleTemplateCancelSuccess = () => {
    onCancelSuccess?.();
  };

  const openCancelConfirmModal = useCallback((): Promise<boolean> => {
    setIsCancelConfirmOpen(true);
    return new Promise(resolve => {
      cancelResolverRef.current = resolve;
    });
  }, []);

  const resolveCancelPromise = useCallback((confirmed: boolean, closeModal = true) => {
    if (cancelResolverRef.current) {
      cancelResolverRef.current(confirmed);
      cancelResolverRef.current = null;
    }
    if (closeModal) {
      setIsCancelConfirmOpen(false);
    }
  }, []);

  const headerData: CourseHeaderData = useMemo(() => {
    const summary = courseDetail.summary;
    return {
      id: summary.id,
      title: summary.title,
      rating: typeof summary.averageRating === 'number' ? summary.averageRating : undefined,
      reviewCount: summary.reviewCount ?? undefined,
      studentCount: summary.enrollmentCount ?? undefined,
      instructor: {
        id: String(summary.instructor?.basicInfo?.id ?? ''),
        username: summary.instructor?.basicInfo?.username ?? '',
        name: summary.instructor?.basicInfo?.displayName ?? summary.instructor?.basicInfo?.username ?? '',
        profileImage: summary.instructor?.basicInfo?.profileImageUrl ?? undefined,
      },
      createdAt: summary.createdAt,
      updatedAt: summary.updatedAt,
      language: undefined,
      enrollmentCount: summary.enrollmentCount?.toString() ?? undefined,
      owned: resolvedOwned,
      progressRate: summary.progressRate != null ? summary.progressRate.toString() : undefined,
      level: summary.level,
      price: summary.price
        ? {
            currency: summary.price.currency ?? 'KRW',
            amount: summary.price.amount?.toString() ?? '0',
          }
        : undefined,
    };
  }, [courseDetail.summary, resolvedOwned]);

  const priceData: CoursePrice | null = useMemo(() => {
    const price = courseDetail.summary?.price;
    if (!price) {
      return null;
    }
    return {
      currency: price.currency ?? 'KRW',
      amount: price.amount?.toString() ?? '0',
      originalAmount: price.originalAmount?.toString(),
      discount: price.discount?.toString(),
      displayPrice: price.displayPrice ?? undefined,
    };
  }, [courseDetail.summary?.price]);

  const headerLanguageSelect = useMemo(() => {
    if (!hasLanguageOptions) {
      return undefined;
    }

    return {
      options: normalizedLanguageOptions.map(language => ({
        value: language,
        label: getLanguageLabel(language),
      })),
      selected: normalizedSelectedLanguage,
      onChange: (languageCode: string) => {
        const normalized = normalizeLanguageCode(languageCode);
        if (normalizedLanguageOptions.includes(normalized)) {
          onLanguageChange?.(normalized);
        }
      },
      isMulti: isMultiLanguage,
      testId: COURSE_DETAIL_TEST_IDS.LANGUAGE_SELECT,
    };
  }, [hasLanguageOptions, isMultiLanguage, normalizedLanguageOptions, normalizedSelectedLanguage, onLanguageChange]);

  type CurriculumLecture = {
    id: string;
    title: string;
    duration?: number | null;
    contentType?: string | null;
    isCompleted?: boolean | null;
    sectionIndex?: number; // For preview mode
    lectureIndex?: number; // For preview mode
  };

  type CurriculumSection = {
    id: string;
    title: string;
    description?: string | null;
    order?: number;
    lectures: CurriculumLecture[];
  };

  const curriculum: CurriculumSection[] = useMemo(() => {
    return (courseDetail.sections ?? []).map((section, sectionIndex) => {
      const sectionId = section.id ? String(section.id) : `${sectionIndex}`;
      return {
        id: sectionId,
        title: section.title ?? `Section ${sectionIndex + 1}`,
        description: section.description ?? null,
        order: section.order,
        lectures: (section.lectures ?? []).map((lecture, lectureIndex) => ({
          id: lecture.id ? String(lecture.id) : `${sectionId}-${lectureIndex}`,
          title: lecture.title ?? `Lecture ${lectureIndex + 1}`,
          duration: lecture.duration ?? null,
          contentType: lecture.contentType ?? 'UNKNOWN',
          isCompleted: lecture.isCompleted ?? null,
          sectionIndex, // Add index for preview mode
          lectureIndex, // Add index for preview mode
        })),
      };
    });
  }, [courseDetail.sections]);

  // 수강 취소 핸들러 - useApi → Orval 마이그레이션
  const handleCancelEnrollment = async (): Promise<boolean> => {
    console.log('🟡 [Template] handleCancelEnrollment START');
    if (!courseIdForCancellation) {
      console.log('🔴 [Template] No courseId - aborting');
      toast.error('강의 ID를 찾을 수 없어 수강 취소를 진행할 수 없습니다.');
      return false;
    }

    console.log('🟡 [Template] Opening confirm modal...');
    const confirmed = await openCancelConfirmModal();
    console.log('🟡 [Template] Modal result:', confirmed);

    if (!confirmed) {
      console.log('🔴 [Template] User rejected cancellation');
      return false;
    }

    console.log('🟢 [Template] User confirmed - proceeding with API call');
    try {
      setIsCancelling(true);

      console.log('🟢 [Template] Calling DELETE /api/courses/{courseId}/enrollments with Orval');

      // Orval 훅을 사용해서 수강 취소 API 호출
      await cancelEnrollmentMutation.mutateAsync({
        courseId: courseIdForCancellation!,
        data: {
          reason: '사용자 요청'
        }
      });

      console.log('✅ [Template] API call succeeded');
      toast.success('수강이 성공적으로 취소되었습니다.');
      handleTemplateCancelSuccess();
      return true;
    } catch (error: any) {
      console.error('🔴 [Template] Failed to cancel enrollment:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        '수강 취소 중 오류가 발생했습니다. 다시 시도해주세요.';
      toast.error(errorMessage);
      return false;
    } finally {
      console.log('🟡 [Template] Cleanup - setting states');
      setIsCancelling(false);
      setIsCancelConfirmOpen(false);
    }
  };

  const courseDescription = visibleCourseDescription(courseDetail.description ?? courseDetail.summary?.description ?? '');
  const trimmedDescription = courseDescription.trim();
  const descriptionLineCount = useMemo(() => {
    if (!trimmedDescription) {
      return 0;
    }
    return courseDescription.split('\n').length;
  }, [courseDescription, trimmedDescription]);
  const hasDescription = trimmedDescription.length > 0;
  const needsDescriptionExpansion = descriptionLineCount > 5;

  const toggleDescription = () => {
    // 축소가 필요할 때만 상태 변경
    if (needsDescriptionExpansion) {
      setIsDescriptionExpanded(!isDescriptionExpanded);
    }
  };

  // 강의 클릭 핸들러 - Preview mode에서는 index 기반 URL 사용
  const handleLectureClick = (courseId: string, lectureId: string, sectionIndex?: number, lectureIndex?: number) => {
    if (isPreviewMode && sectionIndex !== undefined && lectureIndex !== undefined) {
      // Index-based preview URL
      const url = `/learn/course/${courseId}/preview/${sectionIndex}/${lectureIndex}`;
      console.log('🔍 [Preview] Navigating to index-based URL:', url);
      navigate(url);
    } else {
      // Regular published lecture URL
      const url = `/learn/course/${courseId}/lecture/${lectureId}`;
      navigate(url);
    }
  };

  // 편집 버튼 클릭 핸들러
  const handleEditClick = async () => {
    if (!courseDetail.summary?.id) {
      console.error('🔴 [CourseDetailTemplate] No course ID available for edit');
      toast.error('강의 ID를 찾을 수 없습니다.');
      return;
    }

    try {
      console.log('🔵 [CourseDetailTemplate] Fetching draft ID for published course:', courseDetail.summary.id);

      // Published course ID로 draft ID 조회
      const response = await getCourseDraftId(courseDetail.summary.id);

      console.log('🔵 [CourseDetailTemplate] API response received:', {
        response,
        responseType: typeof response,
        hasData: 'data' in response,
        hasDraftId: 'draftId' in response,
      });

      // customInstance는 unwrapped data를 반환 (response.data가 아님)
      const draftId = (response as any).draftId;

      if (!draftId) {
        console.error('❌ [CourseDetailTemplate] No draftId in response:', response);
        toast.error('서버에서 초안 ID를 받지 못했습니다.');
        return;
      }

      const editPath = `/learn/course/${draftId}/edit`;
      console.log('🔵 [CourseDetailTemplate] Draft ID retrieved, navigating to edit', {
        publishedCourseId: courseDetail.summary.id,
        draftId,
        editPath,
        navigationMethod: 'usePrefixedNavigate',
        currentPath: window.location.pathname,
      });

      navigate(editPath);
      console.log('🔵 [CourseDetailTemplate] Navigation called, path:', editPath);
    } catch (error: any) {
      console.error('❌ [CourseDetailTemplate] Failed to get draft ID:', {
        error,
        message: error.message,
        response: error.response,
        stack: error.stack,
      });

      if (error.response?.status === 404) {
        toast.error('이 강의의 초안을 찾을 수 없습니다.');
      } else if (error.response?.status === 403) {
        toast.error('강의를 편집할 권한이 없습니다.');
      } else {
        toast.error('강의 편집 페이지로 이동하는 중 오류가 발생했습니다.');
      }
    }
  };

  return (
    <TemplateContainer data-testid={COURSE_DETAIL_TEST_IDS.ROOT}>
      <CourseHeader
        courseData={headerData}
        priceData={priceData}
        isEnrolled={resolvedOwned}
        isCompleted={isCompleted}
        showCancelButton={resolvedOwned && !isCompleted}
        onEnrollmentSuccess={handleTemplateEnrollmentSuccess}
        onCancelEnrollment={courseIdForCancellation ? handleCancelEnrollment : undefined}
        onReviewsClick={handleNavigateToReviews}
        onAuthorClick={onAuthorClick}
        currentEnrollmentId={currentEnrollmentId}
        languageSelect={headerLanguageSelect}
        isInstructor={isInstructor}
        onEditClick={handleEditClick}
      />

      {/* TODO: Sticky Sidebar 구현 */}

      <MainContent>
        {/* TODO: "What you'll learn" 박스 */}

        {hasDescription && (
          <DescriptionContainer data-testid={COURSE_DETAIL_TEST_IDS.DESCRIPTION_SECTION}>
            <h2>강의 설명</h2> {/* 제목 추가 */}
            <DescriptionContent
              isExpanded={isDescriptionExpanded}
              needsExpansion={needsDescriptionExpansion}
            >
              <p style={{ whiteSpace: 'pre-wrap' }}>{courseDescription}</p>
            </DescriptionContent>
            {needsDescriptionExpansion && (
              <ButtonWrapper>
                <ToggleButton onClick={toggleDescription}>
                  {isDescriptionExpanded ? (
                    <>
                      간략히 보기 <ChevronUp />
                    </>
                  ) : (
                    <>
                      더 보기 <ChevronDown />
                    </>
                  )}
                </ToggleButton>
              </ButtonWrapper>
            )}
          </DescriptionContainer>
        )}

        {/* Curriculum 표시 부분 수정: courseDetail.sections 사용 */}
        {/* sections가 null/undefined일 수 있으므로 확인 */}
        {curriculum.length > 0 && (
          <CourseCurriculum
            curriculum={curriculum}
            isOwned={resolvedOwned || openAccess}
            isInstructor={isInstructor}
            courseId={courseDetail.summary?.id}
            onLectureClick={handleLectureClick}
            checkIsOwned={() => {
              // 클릭 시점의 최신 owned 상태 반환
              const currentBackendOwned = courseDetail.summary?.owned === true;
              return resolveOwnedState(currentBackendOwned, ownedOverride) || courseDetail.summary?.openAccess === true;
            }}
          />
        )}

        {/* TODO: Requirements */}
        {/* TODO: Instructor Info */}

        {/* Reviews Section - 리뷰가 있을 때만 표시 */}
        {!reviewsLoading && hasReviews && (
          <ReviewSection data-testid={COURSE_DETAIL_TEST_IDS.REVIEW_SECTION}>
            <ReviewSectionHeader>
              <h2>수강생 리뷰 {courseDetail.summary?.reviewCount ? `(${courseDetail.summary.reviewCount})` : ''}</h2>
              <ViewAllReviewsButton
                variant="outline"
                size="md"
                onClick={handleNavigateToReviews}
                data-testid={COURSE_DETAIL_TEST_IDS.VIEW_ALL_REVIEWS_BUTTON}
              >
                모든 리뷰 보기
              </ViewAllReviewsButton>
            </ReviewSectionHeader>

            <ReviewList reviews={reviews} />
          </ReviewSection>
        )}

      </MainContent>
      <ConfirmModal
        isOpen={isCancelConfirmOpen}
        title="수강 취소 확인"
        message="정말로 수강을 취소하시겠습니까? 이미 학습한 내용은 저장되지 않을 수 있습니다."
        confirmText="수강 취소"
        cancelText="계속 학습"
        isDangerous={true}
        isLoading={isCancelling}
        onConfirm={() => {
          if (isCancelling) {
            return;
          }
          resolveCancelPromise(true, false);
        }}
        onCancel={() => {
          if (isCancelling) {
            return;
          }
          resolveCancelPromise(false);
        }}
        data-testid="course-cancel-confirm-modal"
      />
      {/* TODO: 앱 전체 푸터 */}
    </TemplateContainer>
  );
};

export default CourseDetailTemplate;
